import {
  Flag,
  addBooking,
  AlertIcon,
  deleteBooking,
  DropZone,
  InvalidInput,
  formatRange,
  formatWhole,
  countryName,
  getPlan,
  live,
  NotFound,
  readLine,
  readPdf,
  readImage,
  updateBooking,
  type Booking,
  type Money,
  type MoneyRange,
  type Draft,
  type Plan,
  type PlanStop,
} from '@trippath/shared';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import { BookingDialog } from '../../components/booking/BookingDialog';
import { TicketCode } from '../../components/booking/TicketCode';
import type { Sector } from '../../components/booking/TicketEditor';
import { dayShort, priceSaid } from '../../lib/format';
import {
  IncidentDialog,
  IncidentMark,
  replacedIn,
  zonesOf,
} from '../../components/booking/Incident';
import { Journeys } from '../../components/way/Journeys';
import { planMap, planRoads } from '../../lib/map';
import { useRoads } from '../../lib/roads';
import { firstPage } from '../../lib/pdfPage';
import { mergeDrafts } from '../../lib/drafts';
import { MapPanel } from '../../components/map/MapPanel';
import { ModeIcon } from '../../components/icons/ModeIcon';
import { StatusIcon } from '../../components/icons/StatusIcon';
import { ticketOf } from '../../lib/wayTicket';
import { useLive } from '../../app/live';
import { ReadOnlySet, useReadOnly } from '../../app/readOnly';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan };

type Editing =
  | { kind: 'none' }
  | { kind: 'new'; draft?: Draft }
  | { kind: 'draft'; draft: Draft; sources?: File[] }
  | { kind: 'edit'; booking: Booking; stop: string; sector: Sector | null }
  | { kind: 'incident'; booking: Booking; stop: PlanStop };

/** Whether the server refused a file for `problem`. */
function refused(error: unknown, problem: string): boolean {
  return error instanceof InvalidInput && error.fields.some((f) => f.problem === problem);
}

/**
 * A confirmation read from its file: a PDF by its own words, a scan's first page and a photo by
 * the server's OCR.
 */
function read(trip: string, file: File): Promise<Draft> {
  if (file.type !== 'application/pdf') return readImage(trip, file);
  return readPdf(trip, file).catch(async (error: unknown) => {
    if (!refused(error, 'scanned')) throw error;
    const url = URL.createObjectURL(file);
    try {
      return await readImage(trip, await firstPage(url));
    } finally {
      URL.revokeObjectURL(url);
    }
  });
}

/** What a file that could not be read says. */
function failureOf(error: unknown): 'pdf' | 'noOcr' | 'server' {
  if (refused(error, 'no_ocr')) return 'noOcr';
  return error instanceof InvalidInput ? 'pdf' : 'server';
}

interface Row {
  booking: Booking;
  stop: string;
  city: string;
  /** The stop it is kept on. */
  holder: PlanStop;
}

/** A row's place in the ledger: its day, or after every day when it has none. */
const when = (row: Row) => row.booking.day ?? '9999-12-31';

type Grouping = 'date' | 'city' | 'country';

/** `18.10`; `18–20.10` within a month, `30.10–2.11` across one. */
function span(from: string, to: string): string {
  const short = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
  if (from === to) return short(from);
  return from.slice(0, 7) === to.slice(0, 7)
    ? `${from.slice(8, 10)}–${short(to)}`
    : `${short(from)}–${short(to)}`;
}

/** A booking's days, a stay from its first night to the morning it leaves; `—` without a day. */
function dates(booking: Booking): string {
  if (booking.day === null) return '—';
  if (booking.kind !== 'stay' || booking.nights === 0) return span(booking.day, booking.day);
  const end = new Date(`${booking.day}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + booking.nights);
  return span(booking.day, end.toISOString().slice(0, 10));
}

/** Nights not yet booked: how many, about what they cost and a night of them. */
interface Gap {
  /** The first stop with a night open: where a stay is booked from it. */
  stop: PlanStop;
  nights: number;
  cost: MoneyRange;
  perNight: MoneyRange;
}

interface Group {
  key: string;
  /** Whose flag is before its title. */
  country: string;
  title: string;
  sub: string;
  dates: string;
  /** What is booked in it; none when nothing is. */
  booked: string | null;
  rows: Row[];
  gap: Gap | null;
  /** Its ways on with no ticket yet: still to book. */
  ways: PlanStop[];
}

/** The stops whose way on has no ticket yet, the way said: transport still to book. */
function openWays(plan: Plan, stops: PlanStop[]): PlanStop[] {
  return stops.filter(
    (stop) =>
      stop.out !== null &&
      plan.stops.indexOf(stop) < plan.stops.length - 1 &&
      ticketOf(plan, stop).ticket === undefined,
  );
}

/** The open nights of some stops, each priced at its own stop's beds; none when all are booked. */
function gapOf(stops: PlanStop[]): Gap | null {
  const open = stops.filter((stop) => stop.openNights > 0);
  const [first] = open;
  if (!first) return null;
  const sum = (pick: (stop: PlanStop) => Money) => ({
    ...pick(first),
    minor: open.reduce((n, stop) => n + pick(stop).minor * stop.openNights, 0),
  });
  const least = (pick: (stop: PlanStop) => Money, most: boolean) => ({
    ...pick(first),
    minor: (most ? Math.max : Math.min)(...open.map((stop) => pick(stop).minor)),
  });
  return {
    stop: first,
    nights: open.reduce((n, stop) => n + stop.openNights, 0),
    cost: {
      min: sum((stop) => stop.accommodation.min),
      max: sum((stop) => stop.accommodation.max),
    },
    perNight: {
      min: least((stop) => stop.accommodation.min, false),
      max: least((stop) => stop.accommodation.max, true),
    },
  };
}

/**
 * The ledger in groups: one per stop, or per country; `null` by date, one list. A stop with
 * nothing in it and no night to book is left out.
 */
function useGroups() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (plan: Plan, rows: Row[], by: Grouping): Group[] | null => {
    if (by === 'date') return null;
    const stops = plan.stops.filter((stop) => !stop.unresolved);
    const group = (key: string, title: string, first: PlanStop, inIt: PlanStop[]): Group => {
      const minor = inIt.reduce((sum, s) => sum + s.cost.paid.minor + s.cost.toPay.minor, 0);
      const ids = inIt.map((stop) => stop.id);
      return {
        key,
        country: first.country,
        title,
        sub: t('bookings.nightsCount', { count: inIt.reduce((sum, s) => sum + s.days, 0) }),
        dates: span(
          first.start,
          inIt.reduce((end, stop) => stop.end, first.end),
        ),
        booked:
          minor === 0
            ? null
            : t('bookings.groupBooked', {
                amount: formatWhole({ ...plan.totals.paid, minor }, lang),
              }),
        rows: rows.filter((row) => ids.includes(row.stop)),
        gap: gapOf(inIt),
        ways: openWays(plan, inIt),
      };
    };
    const groups =
      by === 'city'
        ? stops.map((stop) =>
            group(stop.id, `${stop.city}, ${countryName(stop.country, lang)}`, stop, [stop]),
          )
        : stops
            .filter((stop, i) => stops.findIndex((s) => s.country === stop.country) === i)
            .map((first) =>
              group(
                first.country,
                countryName(first.country, lang),
                first,
                stops.filter((stop) => stop.country === first.country),
              ),
            );
    return groups.filter((g) => g.rows.length > 0 || g.gap !== null || g.ways.length > 0);
  };
}

/** Every booking of a trip in one ledger by day, with what is paid, to pay and still open. */
export function Bookings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [editing, setEditing] = useState<Editing>({ kind: 'none' });
  // A trip read only: its bookings said, none added, read in or changed.
  const locked = useReadOnly();
  const [text, setText] = useState('');
  const [group, setGroup] = useState<Grouping>('city');
  // The ticket whose QR code is shown full size, to the reader at the door.
  const [showing, setShowing] = useState<{
    code: string;
    name: string;
    reference: string | null;
  } | null>(null);
  const codeOf = (booking: Booking) => {
    const code = booking.qr;
    return (
      code !== null && (
        <>
          {' · '}
          <button
            type="button"
            className="link-button"
            aria-label={t('ticketCode.show', { name: booking.name })}
            onClick={() => {
              setShowing({ code, name: booking.name, reference: booking.reference });
            }}
          >
            {t('ticketCode.showShort')}
          </button>
        </>
      )
    );
  };
  const [readFailed, setReadFailed] = useState<'line' | 'pdf' | 'noOcr' | 'server' | null>(
    null,
  );
  // A file being read: a photo's or a scan's words take the OCR some seconds.
  const [reading, setReading] = useState(false);
  const lineId = useId();
  const groupRows = useGroups();
  const [search, setSearch] = useSearchParams();
  // All of them, the beds alone, or the ways by their tickets, leg by leg.
  const asked = search.get('view');
  const view = asked === 'stays' || asked === 'transport' ? asked : 'all';

  useEffect(() => {
    getPlan(id).then(
      (plan) => {
        setState({ kind: 'ready', plan });
      },
      (error: unknown) => {
        setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
  }, [id, changed]);

  if (state.kind === 'loading') {
    return <p className="page quiet">{t('trips.loading')}</p>;
  }
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }

  const { plan } = state;
  const trip = plan.trip.id;
  const close = () => {
    setEditing({ kind: 'none' });
  };
  const saved = (next: Plan) => {
    setState({ kind: 'ready', plan: next });
    setEditing({ kind: 'none' });
  };
  // One ledger across the stops, by day; undated bookings last, as the server orders them.
  const rows: Row[] = plan.stops
    .flatMap((stop) =>
      stop.bookings
        .filter((booking) => view === 'all' || booking.kind === 'stay')
        .map((booking) => ({ booking, stop: stop.id, city: stop.city, holder: stop })),
    )
    .sort((a, b) => when(a).localeCompare(when(b)));
  const booked = {
    ...plan.totals.paid,
    minor: plan.totals.paid.minor + plan.totals.toPay.minor,
  };
  const days = Math.max(plan.totals.days, 1);
  const perDay = {
    min: { ...plan.totals.total.min, minor: Math.round(plan.totals.total.min.minor / days) },
    max: { ...plan.totals.total.max, minor: Math.round(plan.totals.total.max.minor / days) },
  };
  const tickets = rows.filter((row) => row.booking.kind === 'ticket' && live(row.booking));
  const ticketTotal = {
    ...plan.totals.paid,
    minor: tickets.reduce((sum, row) => sum + row.booking.price.minor, 0),
  };
  const groups = groupRows(plan, rows, group);
  const line = (row: Row) => (
    <div
      role="row"
      key={row.booking.id}
      className={live(row.booking) ? 'ledger-row' : 'ledger-row ledger-cancelled'}
    >
      <span role="cell" className="mono">
        {dates(row.booking)}
      </span>
      <span role="cell">
        <strong>{row.city}</strong> ·{' '}
        <button
          type="button"
          className="link-button"
          aria-label={t('bookings.edit', { name: row.booking.name })}
          onClick={() => {
            setEditing({ kind: 'edit', booking: row.booking, stop: row.stop, sector: null });
          }}
        >
          {row.booking.name}
        </button>
        <span className="quiet">
          {' · '}
          {row.booking.kind === 'stay'
            ? t('plan.nightsShort', { count: row.booking.nights })
            : t('bookings.ticket')}
          {row.booking.reference !== null && ` · ${row.booking.reference}`}
          {codeOf(row.booking)}
          {row.booking.link !== null && (
            <>
              {' · '}
              <a
                href={row.booking.link}
                target="_blank"
                rel="noreferrer"
                aria-label={t('bookings.openLink', { name: row.booking.name })}
              >
                {t('plan.openLink')}
              </a>
            </>
          )}
        </span>
        <IncidentMark
          booking={row.booking}
          replaced={replacedIn(plan, row.booking)}
          zones={zonesOf(plan, row.holder)}
        />
        <button
          type="button"
          className="icon-button icon-button-small ledger-incident"
          aria-label={t('incident.open', { name: row.booking.name })}
          title={t('incident.open', { name: row.booking.name })}
          onClick={() => {
            setEditing({ kind: 'incident', booking: row.booking, stop: row.holder });
          }}
        >
          <AlertIcon />
        </button>
      </span>
      <span role="cell" className="mono strong">
        {priceSaid(row.booking.price, row.booking.paid, lang)}
      </span>
      <span role="cell" className="mono">
        {row.booking.kind === 'stay' && row.booking.nights > 0
          ? formatWhole(
              {
                ...row.booking.price,
                minor: Math.round(row.booking.price.minor / row.booking.nights),
              },
              lang,
            )
          : '—'}
      </span>
      <span role="cell">
        {!live(row.booking) ? null : row.booking.pay === 'paid' ? (
          <StatusIcon status="booked" />
        ) : (
          <span
            className="status-icon status-icon-later"
            role="img"
            aria-label={t('bookings.later')}
          >
            €
          </span>
        )}
      </span>
    </div>
  );

  // A way on with no ticket yet: by what, from where to where, about what it costs; pressed, its
  // ticket is added. Among others still to book, it need not say so again.
  const wayOpen = (stop: PlanStop, said = true) => {
    const next = plan.stops[plan.stops.indexOf(stop) + 1];
    const out = stop.out;
    return (
      next &&
      out && (
        <div role="row" key={`${stop.id}-way`} className="ledger-row ledger-gap">
          <span role="cell" className="mono">
            {dayShort(stop.end, lang)}
          </span>
          <span role="cell">
            <button
              type="button"
              className="link-button ledger-way"
              onClick={() => {
                setEditing({
                  kind: 'new',
                  draft: {
                    stop: stop.id,
                    kind: 'ticket',
                    name: `${stop.city} → ${next.city}`,
                    day: stop.end,
                    nights: 0,
                    price: null,
                    currency: null,
                    pay: 'paid',
                    reference: null,
                    link: null,
                  },
                });
              }}
            >
              <ModeIcon mode={out.mode} size={14} />
              {stop.city} → {next.city}
            </button>
            {` · ${t(`mode.${out.mode}`)}`}
            {said && ` · ${t('bookings.wayOpen')}`}
          </span>
          <span role="cell" className="mono">
            {formatRange(out.estimate, lang, true)}
          </span>
          {/* Among the bookings it fills their columns; among the journeys it has none more. */}
          {said && (
            <>
              <span role="cell" className="mono">
                —
              </span>
              <span role="cell" />
            </>
          )}
        </div>
      )
    );
  };

  // Nights still to book: about what they cost, in all and a night, in a quiet row of their own.
  const open = (key: string, when: string, gap: Gap | null, what: ReactNode, after = '') =>
    gap && (
      <div role="row" key={key} className="ledger-row ledger-gap">
        <span role="cell" className="mono">
          {when}
        </span>
        <span role="cell">
          <button
            type="button"
            className="link-button"
            aria-label={t('bookings.bookStay', { city: gap.stop.city })}
            title={t('bookings.bookStay', { city: gap.stop.city })}
            onClick={() => {
              setEditing({
                kind: 'new',
                draft: {
                  stop: gap.stop.id,
                  kind: 'stay',
                  name: '',
                  // A stop with nothing booked starts on its first night; else it is asked.
                  day: gap.stop.bookings.length === 0 ? gap.stop.start : null,
                  nights: gap.stop.openNights,
                  price: null,
                  currency: null,
                  pay: 'paid',
                  reference: null,
                  link: null,
                },
              });
            }}
          >
            {what}
          </button>
          {after}
        </span>
        <span role="cell" className="mono">
          {formatRange(gap.cost, lang, true)}
        </span>
        <span role="cell" className="mono">
          {formatRange(gap.perNight, lang, true)}
        </span>
        <span role="cell">
          <span
            className="status-icon status-icon-open"
            role="img"
            aria-label={t('plan.open')}
            title={t('plan.open')}
          />
        </span>
      </div>
    );

  return (
    <div className="sub-page">
      <div className="sub-head">
        <h1>{t('bookings.title')}</h1>
        <p className="quiet">{t('bookings.lead')}</p>
        <div
          className="segmented booking-views"
          role="tablist"
          aria-label={t('bookings.views')}
        >
          {(['all', 'stays', 'transport'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={view === option}
              onClick={() => {
                setSearch(option === 'all' ? {} : { view: option }, { replace: true });
              }}
            >
              {t(`bookings.view.${option}`)}
            </button>
          ))}
        </div>
        <p className="sub-head-end">
          {t('bookings.bookedSoFar')} <span className="mono">{formatWhole(booked, lang)}</span>
        </p>
      </div>
      {view === 'transport' ? (
        <div className="sub-body">
          <div className="sub-main">
            {/* A trip read only: its journeys said, none opened to change. */}
            <ReadOnlySet>
              <Journeys
                plan={plan}
                onOpen={(booking, stop, sector) => {
                  setEditing({ kind: 'edit', booking, stop, sector });
                }}
              />
            </ReadOnlySet>
            {openWays(plan, plan.stops).length > 0 && !locked && (
              <>
                <div className="ledger-head">
                  <h2 id="ways-open-title">
                    {t('bookings.waysOpen', { count: openWays(plan, plan.stops).length })}
                  </h2>
                </div>
                <section className="panel ledger ledger-ways" aria-labelledby="ways-open-title">
                  <div role="table" aria-labelledby="ways-open-title">
                    <div role="row" className="ledger-cols">
                      <span role="columnheader">{t('plan.date')}</span>
                      <span role="columnheader">{t('bookings.journey')}</span>
                      <span role="columnheader">{t('bookings.estimateCol')}</span>
                    </div>
                    {openWays(plan, plan.stops).map((stop) => wayOpen(stop, false))}
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="sub-body">
          <div className="sub-main">
            {plan.stops.length > 0 && !locked && (
              <section className="panel paste-card" aria-labelledby={`${lineId}-title`}>
                <h2 id={`${lineId}-title`}>{t('bookings.pasteTitle')}</h2>
                <form
                  className="type-line-row"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setReadFailed(null);
                    readLine(trip, text).then(
                      (draft) => {
                        setEditing({ kind: 'draft', draft });
                      },
                      () => {
                        setReadFailed('line');
                      },
                    );
                  }}
                >
                  <label className="visually-hidden" htmlFor={lineId}>
                    {t('bookings.type')}
                  </label>
                  <input
                    id={lineId}
                    className="text-input mono"
                    type="text"
                    value={text}
                    placeholder={t('bookings.typePlaceholder')}
                    aria-describedby={`${lineId}-hint`}
                    onChange={(event) => {
                      setText(event.target.value);
                    }}
                  />
                  <button
                    type="submit"
                    className="button button-primary"
                    disabled={text.trim() === ''}
                  >
                    {t('bookings.read')}
                  </button>
                </form>
                <DropZone
                  label={t('bookings.drop')}
                  choose={t('bookings.choose')}
                  hint={t('bookings.dropHint')}
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  multiple
                  onFile={(file, more) => {
                    setReadFailed(null);
                    setReading(true);
                    // Each file read, those read made one booking: an invoice and its pass.
                    const files = [file, ...more];
                    void Promise.allSettled(files.map((one) => read(trip, one)))
                      .then((answers) => {
                        const [lead, ...rest] = answers.flatMap((answer) =>
                          answer.status === 'fulfilled' ? [answer.value] : [],
                        );
                        if (lead === undefined) {
                          // None read: each refused, the first said.
                          const [refusal] = answers as PromiseRejectedResult[];
                          setReadFailed(failureOf(refusal?.reason));
                          return;
                        }
                        setEditing({
                          kind: 'draft',
                          draft: mergeDrafts([lead, ...rest]),
                          sources: files,
                        });
                      })
                      .finally(() => {
                        setReading(false);
                      });
                  }}
                />
                {reading && (
                  <p className="quiet" role="status">
                    {t('bookings.reading')}
                  </p>
                )}
                <p className="quiet paste-hint" id={`${lineId}-hint`}>
                  {t('bookings.typeHint')}
                </p>
                {readFailed !== null && (
                  <p className="form-failed" role="alert">
                    {t(
                      readFailed === 'pdf'
                        ? 'bookings.pdfFailed'
                        : readFailed === 'noOcr'
                          ? 'bookings.noOcr'
                          : 'bookings.readFailed',
                    )}
                  </p>
                )}
              </section>
            )}

            <div className="ledger-head">
              <h2 id="ledger-title">{t('bookings.count', { count: rows.length })}</h2>
              <div className="group-by" role="group" aria-label={t('bookings.groupBy')}>
                <span className="quiet">{t('bookings.groupBy')}</span>
                {(['date', 'city', 'country'] as const).map((by) => (
                  <button
                    key={by}
                    type="button"
                    aria-pressed={group === by}
                    onClick={() => {
                      setGroup(by);
                    }}
                  >
                    {t(`bookings.by.${by}`)}
                  </button>
                ))}
              </div>
              {plan.stops.length > 0 && !locked && (
                <>
                  <Link className="button button-small" to={`/trips/${trip}/flights`}>
                    {t('flights.link')}
                  </Link>
                  <Link className="button button-small" to={`/trips/${trip}/bookings/mail`}>
                    {t('bookings.fromMail')}
                  </Link>
                  <button
                    type="button"
                    className="button button-small button-primary"
                    onClick={() => {
                      setEditing({ kind: 'new' });
                    }}
                  >
                    {t('bookings.add')}
                  </button>
                </>
              )}
            </div>
            <ReadOnlySet>
              <section className="panel ledger" aria-labelledby="ledger-title">
                {rows.length === 0 && (group === 'date' || plan.stops.length === 0) ? (
                  <p className="quiet">{t('bookings.none')}</p>
                ) : (
                  <div role="table" aria-labelledby="ledger-title">
                    <div role="row" className="ledger-cols">
                      <span role="columnheader">{t('plan.date')}</span>
                      <span role="columnheader">{t('plan.item')}</span>
                      <span role="columnheader">{t('plan.price')}</span>
                      <span role="columnheader">{t('bookings.perDay')}</span>
                      <span role="columnheader">
                        <span className="visually-hidden">{t('plan.status')}</span>
                      </span>
                    </div>
                    {groups === null
                      ? [
                          ...rows.map(line),
                          ...openWays(plan, plan.stops).map((stop) => wayOpen(stop)),
                        ]
                      : groups.map((g) =>
                          g.rows.length === 0 && g.ways.length === 0 ? (
                            open(
                              g.key,
                              g.dates,
                              g.gap,
                              <strong>
                                <Flag country={g.country} />
                                {g.title}
                              </strong>,
                              ` · ${g.sub}`,
                            )
                          ) : (
                            <div role="rowgroup" key={g.key}>
                              <div role="row" className="ledger-group">
                                <span role="cell">
                                  <Flag country={g.country} />
                                  {g.title}
                                  <span className="quiet"> · {g.sub}</span>
                                </span>
                                <span role="cell">{g.booked}</span>
                              </div>
                              {g.rows.map(line)}
                              {open(
                                `${g.key}-open`,
                                '',
                                g.gap,
                                g.gap && t('bookings.gapNights', { count: g.gap.nights }),
                              )}
                              {g.ways.map((stop) => wayOpen(stop))}
                            </div>
                          ),
                        )}
                  </div>
                )}
              </section>
            </ReadOnlySet>
          </div>

          <aside className="sub-side">
            {plan.stops.length > 0 && (
              <section className="panel booked-map" aria-labelledby="booked-map-title">
                <div className="booked-map-head">
                  <h2 id="booked-map-title">{t('plan.route')}</h2>
                  <span>{t('bookings.fromBookings')}</span>
                </div>
                <BookedMap plan={plan} />
                <p className="booked-map-foot">
                  <span>{t('bookings.tickets', { count: tickets.length })}</span>
                  <span className="mono">{formatWhole(ticketTotal, lang)}</span>
                </p>
              </section>
            )}
            <section className="budget-card" aria-labelledby="so-far-title">
              <h2 id="so-far-title">
                {t('bookings.soFar', {
                  from: dayShort(plan.trip.start, lang),
                  to: dayShort(plan.trip.end, lang),
                })}
              </h2>
              <dl className="so-far">
                <div>
                  <dt>{t('bookings.booked')}</dt>
                  <dd>{formatWhole(booked, lang)}</dd>
                </div>
                <div>
                  <dt>{t('bookings.paid')}</dt>
                  <dd className="paid">{formatWhole(plan.totals.paid, lang)}</dd>
                </div>
                <div>
                  <dt>{t('bookings.estimated')}</dt>
                  <dd>{formatRange(plan.totals.estimate, lang, true)}</dd>
                </div>
                <div className="so-far-day">
                  <dt>{t('bookings.perDayDays', { count: plan.totals.days })}</dt>
                  <dd>{formatRange(perDay, lang, true)}</dd>
                </div>
              </dl>
            </section>
          </aside>
        </div>
      )}

      {showing !== null && (
        <TicketCode
          code={showing.code}
          name={showing.name}
          reference={showing.reference}
          onClose={() => {
            setShowing(null);
          }}
        />
      )}
      {editing.kind === 'new' && (
        <BookingDialog
          plan={plan}
          draft={editing.draft}
          save={(input) => addBooking(trip, input)}
          onClose={close}
          onSaved={saved}
        />
      )}
      {editing.kind === 'draft' && (
        <BookingDialog
          plan={plan}
          draft={editing.draft}
          sources={editing.sources}
          save={(input) => addBooking(trip, input)}
          onClose={close}
          onSaved={(next) => {
            setText('');
            saved(next);
          }}
        />
      )}
      {editing.kind === 'incident' && (
        <IncidentDialog
          plan={plan}
          stop={editing.stop}
          booking={editing.booking}
          onClose={close}
          onSaved={saved}
        />
      )}
      {editing.kind === 'edit' && (
        <BookingDialog
          plan={plan}
          booking={{ booking: editing.booking, stop: editing.stop }}
          save={(input) => updateBooking(trip, editing.booking.id, input)}
          remove={() => deleteBooking(trip, editing.booking.id)}
          onClose={close}
          onSaved={saved}
          sector={editing.sector}
        />
      )}
    </div>
  );
}

/** The trip's route beside its bookings, as the plan's map draws it: a bus or a car along its
 * road, not straight across. */
function BookedMap({ plan }: { plan: Plan }) {
  const { t } = useTranslation();
  const roads = useRoads(useMemo(() => planRoads(plan), [plan]));
  return (
    <MapPanel
      data={planMap(plan, roads)}
      label={t('map.label', { places: plan.stops.map((stop) => stop.city).join(' → ') })}
    />
  );
}
